import { Map, OrderedSet } from 'immutable';

import * as types from './types';
import { GET_TEST_TRENDS, GET_TEST_HEALTH, GET_PAST_HEALTH } from '../test-overview/types';
import { getOSName, getBrowserName } from './../../util-platform';
import { MetricsRecord } from './../../records';

const initialState = new Map({
  selectedVersion: new Map({
    title: null,
    items: [],
    index: 0, // used to refresh the pagination if a selected item is changed
  }),
  aggregatedOSNames: new OrderedSet(),
  aggregatedBrowserNames: new OrderedSet(),
  testHealth: new Map({
    totalCases: new MetricsRecord({
      name: 'Total Cases',
    }),
    failing: new MetricsRecord({
      name: 'Consistently Failing',
    }),
    passing: new MetricsRecord({
      name: 'Complete / Passing',
    }),
  }),
});

export const clearSelectedVersion = (state) => state.set(
  'selectedVersion',
  new Map({ title: null, items: [], index: 0 }),
);

export const consolidateMetric = (data, mainKey, errorKey, failKey) => {
  const metric = Object.entries(data[mainKey]).reduce(
    (obj, [name, count]) => ({ ...obj, [name]: { value: count, error: 0, fail: 0 } }),
    {},
  );

  // data[errorKey] can be undefined
  Object.entries(data[errorKey] || {}).forEach(([name, count]) => {
    if (name in metric) {
      metric[name].error += count;
    }
  });

  // data[failKey] can be undefined
  Object.entries(data[failKey] || {}).forEach(([name, count]) => {
    if (name in metric) {
      metric[name].fail += count;
    }
  });

  return metric;
};

export const aggregatePlatformMetrics = (nameToMetricsMap, nameFn) => Object
  .entries(nameToMetricsMap)
  .reduce((acc, [title, metrics]) => {
    const name = nameFn(title);

    if (!(name in acc)) {
      acc[name] = { value: 0, fail: 0, error: 0, items: [] };
    }

    acc[name].value += metrics.value;
    acc[name].fail += metrics.fail;
    acc[name].error += metrics.error;
    acc[name].items.push({ title, ...metrics });

    return acc;
  }, {});

export const aggregateTestHealth = (testCases) => {
  const totalCases = testCases.length;
  let failing = 0;
  let passing = 0;
  testCases.forEach((test) => {
    if (test.total_runs === test.failed_tests) {
      failing += 1;
    } else {
      passing += 1;
    }
  });

  return { passing, failing, totalCases };
};

const testSummaryReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case GET_TEST_TRENDS.SUCCESS:
      return state.withMutations((newState) => {
        clearSelectedVersion(newState);

        const osMetrics = consolidateMetric(payload.metrics, 'os', 'osError', 'osFail');
        const aggregatedOSNames = aggregatePlatformMetrics(osMetrics, getOSName);
        newState.set(
          'aggregatedOSNames',
          new OrderedSet(
            Object
              .entries(aggregatedOSNames)
              .map(([title, aggs]) => ({ title, ...aggs }))
              .sort((item1, item2) => item1.value < item2.value),
          ),
        );

        const browserMetrics = consolidateMetric(payload.metrics, 'browser', 'browserError', 'browserFail');
        const aggregatedBrowserNames = aggregatePlatformMetrics(browserMetrics, getBrowserName);
        newState.set(
          'aggregatedBrowserNames',
          new OrderedSet(
            Object
              .entries(aggregatedBrowserNames)
              .map(([title, aggs]) => ({ title, ...aggs }))
              .sort((item1, item2) => item1.value < item2.value),
          ),
        );
      });
    case types.SELECT_BY_VERSION:
      return state.set('selectedVersion', new Map(payload));
    case types.CLEAR_SELECT_BY_VERSION:
      return clearSelectedVersion(state);
    case GET_TEST_HEALTH.SUCCESS:
      return state.withMutations((newState) => {
        const metrics = aggregateTestHealth(payload.items);
        newState.setIn(['testHealth', 'totalCases', 'value'], metrics.totalCases);
        newState.setIn(['testHealth', 'failing', 'value'], metrics.failing);
        newState.setIn(['testHealth', 'passing', 'value'], metrics.passing);
      });
    case GET_PAST_HEALTH.SUCCESS:
      return state.withMutations((newState) => {
        const metrics = aggregateTestHealth(payload.items);

        newState.setIn(['testHealth', 'totalCases', 'previousValue'], metrics.totalCases);
        newState.setIn(['testHealth', 'failing', 'previousValue'], metrics.failing);
        newState.setIn(['testHealth', 'passing', 'previousValue'], metrics.passing);
      });
    default:
      return state;
  }
};

export default testSummaryReducer;
export { initialState };
